import React, { useCallback, useEffect, useRef, useState } from "react";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const REACTION_LAUNCH_GAP_MS = 150;
const REACTION_LANES = {
  left: [8, 13, 18, 22],
  right: [83, 87, 91, 94],
  spread: [14, 28, 42, 58, 72, 86],
};

const splitReactionEmoji = (emoji) => {
  const value = String(emoji || "\u2764\ufe0f");
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    return [...segmenter.segment(value)].map(({ segment }) => segment).filter(Boolean);
  }
  return [value];
};

const decorateReaction = (reaction, index, side, launchDelay = 0) => {
  const normalizedSide = REACTION_LANES[side] ? side : "spread";
  const lanes = REACTION_LANES[normalizedSide];
  const lane = lanes[index % lanes.length];
  const sway = Math.round(normalizedSide === "right" ? 6 + Math.random() * 10 : 10 + Math.random() * 20);
  const direction = side === "right" ? -1 : (Math.random() > 0.5 ? 1 : -1);
  const defaultStartX = lane + (Math.random() - 0.5) * 1.6;
  const startX = side === "left"
    ? clamp(defaultStartX, 6, 24)
    : side === "right"
      ? clamp(defaultStartX, 81, 95)
      : clamp(Number(reaction.startX) || defaultStartX, 6, 92);
  return {
    ...reaction,
    id: String(reaction.id ?? `${Date.now()}-${index}-${Math.random()}`),
    startX,
    sway: sway * direction,
    rotation: Math.round((5 + Math.random() * 8) * direction),
    duration: Math.round(3300 + Math.random() * 500),
    launchDelay,
    swayDuration: Math.round(1500 + Math.random() * 500),
  };
};

const ReactionParticle = React.memo(({ reaction, onComplete }) => {
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  useEffect(() => {
    const fallback = window.setTimeout(
      () => completeRef.current?.(reaction.id),
      reaction.launchDelay + reaction.duration + 500,
    );
    return () => window.clearTimeout(fallback);
  }, [reaction.duration, reaction.id, reaction.launchDelay]);

  return (
    <span
      className="showme-reaction-flight absolute block select-none"
      style={{
        left: `${reaction.startX}%`,
        bottom: "clamp(96px, 12%, 148px)",
        zIndex: 40,
        "--reaction-duration": `${reaction.duration}ms`,
        "--reaction-delay": `${reaction.launchDelay}ms`,
        "--reaction-sway-duration": `${reaction.swayDuration}ms`,
        "--reaction-sway": `${reaction.sway}px`,
        "--reaction-sway-start": `${reaction.sway * -1}px`,
        "--reaction-rotation": `${reaction.rotation}deg`,
        "--reaction-rotation-start": `${reaction.rotation * -1}deg`,
      }}
      onAnimationEnd={() => onComplete(reaction.id)}
      aria-hidden="true"
    >
      <span
        className="showme-reaction-glyph"
        style={{
          color: reaction.glyphColor,
          fontSize: reaction.glyphSize ? `${reaction.glyphSize}px` : undefined,
          fontWeight: reaction.glyphWeight,
          textShadow: reaction.glyphShadow,
        }}
      >
        {reaction.emoji || "\u2764\ufe0f"}
      </span>
    </span>
  );
});

/** Shared, transform-only reaction renderer used by viewers and creators. */
const LiveReactionLayer = ({
  reactions = [],
  className = "",
  side = "spread",
  launchGapMs = REACTION_LAUNCH_GAP_MS,
  launchJitterMs = 90,
  maxActive = 60,
  dataVariant = "reactions",
}) => {
  const [active, setActive] = useState([]);
  const seenRef = useRef(new Set());
  const laneIndexRef = useRef(0);
  const launchQueueRef = useRef([]);
  const launchTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const launchNextReaction = useCallback(() => {
    if (launchTimerRef.current !== null || !launchQueueRef.current.length) return;

    const launchNext = () => {
      if (!mountedRef.current) return;

      const nextReaction = launchQueueRef.current.shift();
      if (nextReaction) {
        setActive((current) => [...current, nextReaction].slice(-maxActive));
      }

      if (launchQueueRef.current.length) {
        launchTimerRef.current = window.setTimeout(() => {
          launchTimerRef.current = null;
          launchNext();
        }, launchGapMs + Math.round(Math.random() * launchJitterMs));
      } else {
        launchTimerRef.current = null;
      }
    };

    launchNext();
  }, [launchGapMs, launchJitterMs, maxActive]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (launchTimerRef.current !== null) {
        window.clearTimeout(launchTimerRef.current);
      }
      launchTimerRef.current = null;
      launchQueueRef.current = [];
    };
  }, []);

  useEffect(() => {
    const fresh = [];
    reactions.forEach((reaction, index) => {
      const id = String(reaction.id ?? `${reaction.emoji || "reaction"}-${index}`);
      if (seenRef.current.has(id)) return;
      seenRef.current.add(id);
      splitReactionEmoji(reaction.emoji).forEach((emoji, emojiIndex, emojiParts) => {
        const particleId = emojiParts.length > 1 ? `${id}-${emojiIndex}` : id;
        fresh.push(decorateReaction(
          { ...reaction, id: particleId, emoji },
          laneIndexRef.current,
          side,
          0,
        ));
        laneIndexRef.current += 1;
      });
    });

    if (seenRef.current.size > 600) {
      // Retain every particle ID still present in the source prop. Trimming an
      // arbitrary tail can forget older IDs that remain rendered and replay
      // them when a large dollar burst is followed by another donation.
      const retainedIds = new Set();
      reactions.forEach((reaction, index) => {
        const id = String(reaction.id ?? `${reaction.emoji || "reaction"}-${index}`);
        const emojiParts = splitReactionEmoji(reaction.emoji);
        emojiParts.forEach((_, emojiIndex) => {
          retainedIds.add(emojiParts.length > 1 ? `${id}-${emojiIndex}` : id);
        });
      });
      seenRef.current = retainedIds;
    }
    if (fresh.length) {
      // Mount particles one at a time instead of mounting an entire burst with
      // CSS delays. Under streaming load, delayed animation frames can resume
      // together and visually bundle reactions on creator/viewer screens.
      launchQueueRef.current.push(...fresh);
      launchNextReaction();
    }
  }, [launchNextReaction, reactions, side]);

  const removeReaction = useCallback((id) => {
    setActive((current) => current.filter((reaction) => reaction.id !== id));
  }, []);

  return (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
      style={{ contain: "layout style paint", isolation: "isolate" }}
      data-testid="live-reaction-layer"
      data-variant={dataVariant}
    >
      <style>{`
        .showme-reaction-flight {
          opacity: 0;
          transform: translate3d(-50%, 14px, 0);
          backface-visibility: hidden;
          will-change: transform, opacity;
          animation: showMeReactionRise var(--reaction-duration) cubic-bezier(.18,.68,.22,1) forwards;
          animation-delay: var(--reaction-delay);
        }

        .showme-reaction-glyph {
          display: block;
          font-size: clamp(2.25rem, 5vw, 4rem);
          line-height: 1;
          text-shadow: 0 4px 18px rgba(0,0,0,.52), 0 0 14px rgba(96,165,250,.28);
          transform: translate3d(var(--reaction-sway-start), 0, 0) rotate(var(--reaction-rotation-start));
          transform-origin: center;
          backface-visibility: hidden;
          will-change: transform;
          animation: showMeReactionSway var(--reaction-sway-duration) ease-in-out infinite;
        }

        @keyframes showMeReactionRise {
          0% {
            opacity: 0;
            transform: translate3d(-50%, 14px, 0);
          }
          8% {
            opacity: 1;
            transform: translate3d(-50%, -2vh, 0);
          }
          72% {
            opacity: 1;
            transform: translate3d(-50%, -39vh, 0);
          }
          100% {
            opacity: 0;
            transform: translate3d(-50%, -50vh, 0);
          }
        }

        @keyframes showMeReactionSway {
          0%, 100% {
            transform: translate3d(var(--reaction-sway-start), 0, 0) rotate(var(--reaction-rotation-start));
          }
          50% {
            transform: translate3d(var(--reaction-sway), 0, 0) rotate(var(--reaction-rotation));
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .showme-reaction-flight {
            animation-duration: 1600ms;
            animation-delay: 0ms;
          }
          .showme-reaction-glyph {
            animation: none;
          }
        }
      `}</style>

      {active.map((reaction) => (
        <ReactionParticle key={reaction.id} reaction={reaction} onComplete={removeReaction} />
      ))}
    </div>
  );
};

export default LiveReactionLayer;
